import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  readEvents,
  request,
  SetupError,
  type Snapshot,
  type SessionPage,
  type TurnCreate,
  type DecisionCreate,
  type Event,
  type LiveText,
} from '@/api/setup-chat'

const active = (snapshot: Snapshot) =>
  snapshot.runs.find((run) => run.role === 'parent')
export function hasUnresolvedWork(snapshot: Snapshot) {
  return (
    snapshot.runs.length > 0 ||
    snapshot.questions.some((q) => q.status === 'open') ||
    snapshot.approvals.some((a) =>
      ['pending', 'approved', 'applying', 'unknown'].includes(a.status),
    )
  )
}
type Pending = {
  kind: 'create' | 'turn' | 'decision' | 'cancel'
  id: string
  session?: string
  replaces?: string | null
  approval?: string
  run?: string
}
const pendingKey = 'setup_pending_request'
function savePending(value: Pending | null) {
  if (value) sessionStorage.setItem(pendingKey, JSON.stringify(value))
  else sessionStorage.removeItem(pendingKey)
}
function readPending(): Pending | null {
  try {
    return JSON.parse(
      sessionStorage.getItem(pendingKey) || 'null',
    ) as Pending | null
  } catch {
    return null
  }
}
type Summary = SessionPage['items'][number]
const byNewest = (items: Summary[]) =>
  [...items].sort((a, b) => b.created_at.localeCompare(a.created_at))
// Every chat covers all of the person's inboxes.
export function useSetupChat() {
  const auth = useAuth()
  const tokenRef = useRef(auth.getAccessToken)
  useEffect(() => {
    tokenRef.current = auth.getAccessToken
  }, [auth.getAccessToken])
  const getAccessToken = useCallback(() => tokenRef.current(), [])
  const queryClient = useQueryClient()
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [connection, setConnection] = useState<
    'connected' | 'connecting' | 'lost'
  >('connecting')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [uncertain, setUncertain] = useState(false)
  const [canRetryCreate, setCanRetryCreate] = useState(false)
  const [sessions, setSessions] = useState<Summary[]>([])
  // The message being sent, shown at once and replaced by the saved one.
  const [outgoing, setOutgoing] = useState<{ id: string; text: string } | null>(
    null,
  )
  const [lastSentId, setLastSentId] = useState<string | null>(null)
  const current = useRef<Snapshot | null>(null)
  const cursor = useRef(0)
  const pendingTurn = useRef<TurnCreate | null>(null)
  const pendingDecision = useRef<{ id: string; body: DecisionCreate } | null>(
    null,
  )
  const sending = useRef(false)
  const install = useCallback((next: Snapshot, force = false) => {
    if (
      !force &&
      current.current?.id === next.id &&
      next.last_event_seq < cursor.current
    )
      return
    // A reply still streaming lives only here until it is saved; keep it across reloads.
    const previous = current.current
    if (previous?.id === next.id && next.runs.some((r) => r.role === 'parent')) {
      const drafts = previous.messages.filter(
        (m) => m.status === 'streaming' && !next.messages.some((n) => n.id === m.id),
      )
      if (drafts.length)
        next = {
          ...next,
          messages: [...next.messages, ...drafts],
          timeline: [
            ...next.timeline,
            ...previous.timeline.filter(
              (item) =>
                item.kind === 'message' && drafts.some((d) => d.id === item.id),
            ),
          ],
        }
    }
    current.current = next
    cursor.current = next.last_event_seq
    setSnapshot(next)
  }, [])
  const refresh = useCallback(
    async (force = false) => {
      if (!current.current) return
      const token = await getAccessToken()
      const id = current.current.id
      const value = await request<Snapshot>(token, `/sessions/${id}`)
      if (current.current?.id === id) install(value, force)
    },
    [getAccessToken, install],
  )
  const listSessions = useCallback(async () => {
    const token = await getAccessToken()
    let path = '/sessions?limit=100'
    const all: Summary[] = []
    do {
      const page = await request<SessionPage>(token, path)
      all.push(...page.items)
      path = page.next_cursor
        ? `/sessions?limit=100&cursor=${encodeURIComponent(page.next_cursor)}`
        : ''
    } while (path)
    return all
  }, [getAccessToken])
  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setOutgoing(null)
      current.current = null
      cursor.current = 0
      setSnapshot(null)
      try {
        const all = await listSessions()
        const opened = sessionStorage.getItem('setup_opened_past')
        const pending = readPending()
        // Prefer this tab's chat while it is still current, or when it was a
        // past chat opened on purpose; otherwise open the newest chat that New
        // chat has not replaced.
        const live = all.filter((s) => !s.replaced_by)
        const selected = sessionStorage.getItem('setup_selected_session')
        const latest =
          all.find(
            (s) =>
              s.id === pending?.session ||
              (pending?.kind === 'create' &&
                s.client_request_id === pending.id),
          ) ??
          all.find(
            (s) => s.id === selected && (!s.replaced_by || s.id === opened),
          ) ??
          byNewest(live)[0] ??
          byNewest(all)[0]
        if (!cancelled) setSessions(byNewest(all))
        if (latest) {
          const value = await request<Snapshot>(
            await getAccessToken(),
            `/sessions/${latest.id}`,
          )
          if (!cancelled) {
            install(value)
            sessionStorage.setItem('setup_selected_session', value.id)
          }
        }
        if (!cancelled) setConnection('connected')
        if (!cancelled && readPending()) {
          setUncertain(true)
          setError(
            'A previous request needs a status check. Refresh before continuing.',
          )
        }
      } catch {
        if (!cancelled) {
          setError('Could not load setup. Try again.')
          setConnection('lost')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [getAccessToken, install, listSessions])
  const refreshSessions = useCallback(async () => {
    const all = await listSessions()
    setSessions(byNewest(all))
    return all
  }, [listSessions])
  // Switching only changes what is shown; work in the other chat keeps running
  // on the server and is visible again when that chat is reopened.
  const chooseSession = useCallback(
    async (id: string) => {
      if (sending.current || busy || loading || current.current?.id === id)
        return
      sending.current = true
      setBusy(true)
      setError('')
      try {
        const next = await request<Snapshot>(
          await getAccessToken(),
          `/sessions/${id}`,
        )
        install(next, true)
        sessionStorage.setItem('setup_selected_session', next.id)
        sessionStorage.setItem('setup_opened_past', next.id)
      } catch {
        setError('Could not open that chat. Try again.')
      } finally {
        sending.current = false
        setBusy(false)
      }
    },
    [busy, loading, getAccessToken, install],
  )
  // One call: the server stops anything still running in the replaced chat,
  // expires its open approvals and returns a fresh empty chat. A create whose response was lost is retried with the same ID.
  const newSession = useCallback(async () => {
    const state = current.current
    if (sending.current || busy || loading || !state) return
    sending.current = true
    setBusy(true)
    setError('')
    const previous = readPending()
    const pending: Pending =
      previous?.kind === 'create' && previous.replaces === state.id
        ? previous
        : {
            kind: 'create',
            id: crypto.randomUUID(),
            replaces: state.id,
          }
    savePending(pending)
    try {
      const next = await request<Snapshot>(await getAccessToken(), '/sessions', {
        client_request_id: pending.id,
        replaces_session_id: pending.replaces ?? null,
      })
      install(next, true)
      sessionStorage.setItem('setup_selected_session', next.id)
      savePending(null)
      pendingTurn.current = null
      pendingDecision.current = null
      setUncertain(false)
      setCanRetryCreate(false)
      await refreshSessions().catch(() => {})
    } catch (e) {
      if (e instanceof SetupError && e.status >= 400 && e.status < 500) {
        savePending(null)
        setError('Could not start a new chat. Try again.')
      } else {
        setUncertain(true)
        setError('Could not confirm the new chat. Check again before continuing.')
      }
    } finally {
      sending.current = false
      setBusy(false)
    }
  }, [busy, loading, getAccessToken, refreshSessions, install])
  const sessionId = snapshot?.id
  useEffect(() => {
    if (!sessionId) return
    const subscribedSession = sessionId
    const controller = new AbortController()
    let delay = 500
    // Reply text arrives live, piece by piece. A piece that doesn't follow on from
    // what is shown (the stream reconnected mid-reply) turns the reply into "…" until
    // the saved reply replaces it.
    const live = (piece: LiveText) => {
      const state = current.current
      if (!state) return
      const old = state.messages.find((m) => m.id === piece.message_id)
      if (old?.status === 'complete') return
      const follows = (old ? (old.offset ?? -1) : 0) === piece.offset
      const message: Snapshot['messages'][number] = {
        id: piece.message_id,
        attempt_id: piece.message_id,
        role: 'assistant',
        text: follows ? (old?.text ?? '') + piece.text : '…',
        status: 'streaming',
        offset: follows ? piece.offset + piece.text.length : -1,
      }
      if (old && !follows && old.offset === -1) return
      const next = {
        ...state,
        timeline: old
          ? state.timeline
          : [
              ...state.timeline,
              {
                kind: 'message' as const,
                id: piece.message_id,
                seq: state.last_event_seq + 1,
                turn_id: null,
                created_at: new Date().toISOString(),
              },
            ],
        messages: old
          ? state.messages.map((m) => (m.id === piece.message_id ? message : m))
          : [...state.messages, message],
      }
      current.current = next
      setSnapshot(next)
    }
    const process = (event: Event | LiveText) => {
      if (current.current?.id !== subscribedSession) return
      delay = 500
      if (event.type === 'live.delta') return live(event)
      if (event.seq <= cursor.current) return
      if (event.seq !== cursor.current + 1) throw new SetupError(409)
      cursor.current = event.seq
      if (event.type === 'assistant.delta') {
        const state = current.current
        if (!state) return
        const p = event.payload
        const old = state.messages.find((m) => m.id === p.message_id)
        const text = old?.attempt_id === p.attempt_id ? old.text : ''
        if (text.length !== p.offset) throw new SetupError(409)
        const message: Snapshot['messages'][number] = {
          id: p.message_id,
          attempt_id: p.attempt_id,
          role: 'assistant',
          text: text + p.text,
          status: 'streaming',
          offset: p.offset + p.text.length,
        }
        const timeline = state.timeline.some(
          (item) => item.kind === 'message' && item.id === p.message_id,
        )
          ? state.timeline
          : [
              ...state.timeline,
              {
                kind: 'message' as const,
                id: p.message_id,
                seq: event.seq,
                turn_id: event.turn_id,
                created_at: event.created_at,
              },
            ]
        const next = {
          ...state,
          last_event_seq: event.seq,
          timeline,
          messages: old
            ? state.messages.map((m) => (m.id === p.message_id ? message : m))
            : [...state.messages, message],
        }
        current.current = next
        setSnapshot(next)
      } else {
        // The server projection includes decisions, drafts and active runs.
        void refresh().catch(() => setConnection('lost'))
        if (event.type === 'change.saved' && event.payload.status === 'saved') {
          void queryClient.invalidateQueries({ queryKey: ['instructions'] })
          void queryClient.invalidateQueries({ queryKey: ['inboxes'] })
          void queryClient.invalidateQueries({
            queryKey: ['onboarding', 'status'],
          })
        }
      }
    }
    async function subscribe() {
      while (!controller.signal.aborted) {
        try {
          setConnection('connecting')
          const token = await getAccessToken()
          setConnection('connected')
          await readEvents(
            token,
            subscribedSession,
            cursor.current,
            controller.signal,
            process,
          )
        } catch (e) {
          if (controller.signal.aborted) break
          if (e instanceof SetupError && e.status === 409)
            await refresh(true).catch(() => {})
          setConnection('lost')
        }
        if (!controller.signal.aborted) {
          await new Promise<void>((resolve) => {
            const finish = () => {
              window.clearTimeout(timer)
              controller.signal.removeEventListener('abort', finish)
              resolve()
            }
            const timer = window.setTimeout(finish, delay)
            controller.signal.addEventListener('abort', finish, { once: true })
          })
          delay = Math.min(delay * 2, 8000)
        }
      }
    }
    void subscribe()
    return () => controller.abort()
  }, [sessionId, getAccessToken, refresh, queryClient])
  const send = useCallback(
    async (message: string, questionId?: string) => {
      if (sending.current || busy || uncertain || loading || !message.trim())
        return false
      sending.current = true
      setBusy(true)
      setError('')
      const turnId = crypto.randomUUID()
      setOutgoing({ id: turnId, text: message.trim() })
      setLastSentId(turnId)
      try {
        const token = await getAccessToken()
        let state = current.current
        if (!state) {
          // Creation is tied to the first explicit message, never navigation.
          const id = crypto.randomUUID()
          savePending({ kind: 'create', id })
          state = await request<Snapshot>(token, '/sessions', {
            client_request_id: id,
          })
          savePending(null)
          install(state)
          sessionStorage.setItem('setup_selected_session', state.id)
        }
        const body: TurnCreate = {
          client_request_id: turnId,
          expected_session_version: state.version,
          message: message.trim(),
          question_id: questionId ?? null,
        }
        pendingTurn.current = body
        savePending({
          kind: 'turn',
          id: body.client_request_id,
          session: state.id,
        })
        await request(token, `/sessions/${state.id}/turns`, body)
        pendingTurn.current = null
        await refresh()
        savePending(null)
        return true
      } catch (e) {
        if (e instanceof SetupError && e.status >= 400 && e.status < 500) {
          setOutgoing(null)
          await refresh().catch(() => {})
          pendingTurn.current = null
          savePending(null)
          setError(
            'Setup changed or the message could not be sent. Review it and try again.',
          )
        } else {
          setUncertain(true)
          setError(
            'We could not confirm that message. Review the conversation before trying again.',
          )
        }
        return false
      } finally {
        sending.current = false
        setBusy(false)
      }
    },
    [busy, uncertain, loading, getAccessToken, install, refresh],
  )
  const decide = useCallback(
    async (id: string, revision: number, decision: 'approve' | 'deny') => {
      if (sending.current || busy || uncertain || !current.current) return
      sending.current = true
      setBusy(true)
      setError('')
      const body: DecisionCreate = {
        decision_id: crypto.randomUUID(),
        expected_revision: revision,
        decision,
      }
      pendingDecision.current = { id, body }
      savePending({
        kind: 'decision',
        id: body.decision_id,
        session: current.current.id,
        approval: id,
      })
      try {
        const token = await getAccessToken()
        await request(
          token,
          `/sessions/${current.current.id}/approvals/${id}/decision`,
          body,
        )
        pendingDecision.current = null
        await refresh()
        savePending(null)
      } catch (e) {
        if (e instanceof SetupError && e.status >= 400 && e.status < 500) {
          pendingDecision.current = null
          savePending(null)
          await refresh().catch(() => {})
          setError(
            'This request changed or expired. Ask for an updated review.',
          )
        } else {
          setUncertain(true)
          setError(
            'We could not confirm your decision. Review the latest status before continuing.',
          )
        }
      } finally {
        sending.current = false
        setBusy(false)
      }
    },
    [busy, uncertain, getAccessToken, refresh],
  )
  const reconcile = useCallback(async () => {
    try {
      const pending = readPending()
      if (pending?.kind === 'create') {
        const found = (await listSessions()).find(
          (s) => s.client_request_id === pending.id,
        )
        if (found) {
          install(
            await request<Snapshot>(
              await getAccessToken(),
              `/sessions/${found.id}`,
            ),
          )
          savePending(null)
          sessionStorage.setItem('setup_selected_session', found.id)
          setUncertain(false)
          setCanRetryCreate(false)
          setError('The conversation is ready. Review it before continuing.')
          await refreshSessions().catch(() => {})
          return
        }
        setCanRetryCreate(true)
        setError(
          'Could not find the new chat. Check again or try starting it again.',
        )
        return
      }
      await refresh()
      const state = current.current
      const turnConfirmed =
        pending?.kind === 'turn' &&
        state?.messages.some(
          (m) => m.role === 'user' && m.client_request_id === pending.id,
        )
      const decisionConfirmed =
        pending?.kind === 'decision' &&
        state?.approvals.some(
          (a) => a.id === pending.approval && a.decision_id === pending.id,
        )
      const cancelConfirmed =
        pending?.kind === 'cancel' &&
        state &&
        (!state.runs.some((r) => r.id === pending.run) ||
          state.runs.some((r) => r.id === pending.run && r.cancel_requested))
      if (turnConfirmed || decisionConfirmed || cancelConfirmed) {
        savePending(null)
        setUncertain(false)
        pendingTurn.current = null
        pendingDecision.current = null
        setError('Review the latest conversation before continuing.')
      } else
        setError(
          'The outcome is still unknown. Refresh again to check before continuing.',
        )
    } catch {
      setError('Could not refresh setup. Try again.')
    }
  }, [refresh, listSessions, refreshSessions, getAccessToken, install])
  const retryCreate = useCallback(async () => {
    const pending = readPending()
    if (sending.current || !canRetryCreate || pending?.kind !== 'create') return
    sending.current = true
    setBusy(true)
    try {
      const next = await request<Snapshot>(await getAccessToken(), '/sessions', {
        client_request_id: pending.id,
        replaces_session_id: pending.replaces ?? null,
      })
      install(next, true)
      sessionStorage.setItem('setup_selected_session', next.id)
      savePending(null)
      setUncertain(false)
      setCanRetryCreate(false)
      setError('')
      await refreshSessions().catch(() => {})
    } catch {
      setError('Could not confirm the new chat. Check again before continuing.')
      setCanRetryCreate(false)
    } finally {
      sending.current = false
      setBusy(false)
    }
  }, [canRetryCreate, getAccessToken, install, refreshSessions])
  const stop = useCallback(async () => {
    const state = current.current
    const run = state && active(state)
    if (!state || !run || busy || uncertain) return
    setBusy(true)
    try {
      const token = await getAccessToken()
      const id = crypto.randomUUID()
      savePending({ kind: 'cancel', id, session: state.id, run: run.id })
      await request(token, `/sessions/${state.id}/runs/${run.id}/cancel`, {
        client_request_id: id,
      })
      await refresh()
      savePending(null)
    } catch {
      setError(
        'Could not confirm the stop request. Refresh to check its status.',
      )
      setUncertain(true)
    } finally {
      setBusy(false)
    }
  }, [busy, uncertain, getAccessToken, refresh])
  // Drop the outgoing bubble once the saved message is in the conversation.
  const sent =
    !!outgoing &&
    !!snapshot?.messages.some(
      (m) => m.role === 'user' && m.client_request_id === outgoing.id,
    )
  return {
    snapshot,
    outgoing: sent ? null : outgoing,
    lastSentId,
    loading,
    connection,
    error,
    busy,
    uncertain,
    send,
    decide,
    reconcile,
    stop,
    getAccessToken,
    sessions,
    refreshSessions,
    chooseSession,
    newSession,
    canRetryCreate,
    retryCreate,
  }
}
